import { AfterViewInit, Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FLOATING_DIALOG_LAYOUT_KEYS } from 'src/app/core/services/floating-dialog-layout.service';
import { IcdSuggestionsDialogComponent } from '../../patient-file/icd-suggestions-dialog/icd-suggestions-dialog.component';

export interface IcdReviewWizardPatient {
  patientId: string;
  name?: string;
  surname?: string;
  patientNumber?: string;
  counts?: { total?: number;[action: string]: any };
}

export interface IcdReviewWizardData {
  patients: IcdReviewWizardPatient[];
  /** What the user was trying to do when this interrupted them, e.g. 'Export Billing'. */
  exportLabel: string;
}

export interface IcdReviewWizardResult {
  /** True when anything was accepted or rejected — the caller should refresh. */
  changed: boolean;
  /** True when the user asked to run the interrupted export now. */
  runExport: boolean;
}

/**
 * Walks the patients in a billing export through the ICD-10 review queue, one at a time.
 *
 * ── WHY A WIZARD AND NOT "OPEN EACH FILE" ────────────────────────────────────────────
 *
 * ICD-10 drives the claim, so exporting with unreviewed proposals behind it bills a coding set
 * the practice has not agreed to. The alternative is to send the biller to eleven patient files
 * and back, which is enough friction that the honest outcome is the warning gets dismissed.
 *
 * ── WHY IT REUSES THE REVIEW DIALOG'S COMPONENT ──────────────────────────────────────
 *
 * `IcdSuggestionsDialogComponent` is embedded, not reimplemented. The review surface carries the
 * coupling rules (§11.13), the coding-set diff, the §7 rule 6 bulk guard and the audit trail — a
 * second implementation would drift from all four, and the ones that matter are safety rules.
 * `setPatient()` exists on it for exactly this: one instance, many patients, so a dialog the user
 * has dragged and resized does not jump home on every Next.
 *
 * ── WHY IT IS MODELESS ───────────────────────────────────────────────────────────────
 *
 * `hasBackdrop: false` plus the drag/resize directives, the same as the AI assistant. That is not
 * only ergonomics: a decision often cannot be made from the queue alone, so `openPatientFile()`
 * routes underneath the dialog and leaves it open. A MatDialog lives in an overlay and survives
 * router navigation, so the wizard is still there when they come back from reading the notes.
 *
 * ⚠ Navigating away DOES destroy the billing page that opened this. The export intent is lost
 * with it, which is the agreed behaviour: the user re-triggers the export when they return.
 */
@Component({
  selector: 'app-icd-review-wizard',
  templateUrl: './icd-review-wizard.component.html',
  styleUrls: ['./icd-review-wizard.component.scss'],
})
export class IcdReviewWizardComponent implements AfterViewInit, OnDestroy {
  @ViewChild('review') review?: IcdSuggestionsDialogComponent;

  /** Where the dragged/resized bounds are remembered. */
  readonly layoutKey = FLOATING_DIALOG_LAYOUT_KEYS.icdReviewWizard;

  patients: IcdReviewWizardPatient[] = [];
  index = 0;
  exportLabel = 'export';

  /** Accumulated across every patient, so the caller knows whether to refetch anything. */
  changed = false;

  /** Patients whose queue was empty when we last looked — drives the filled dots. */
  private clearedIds = new Set<string>();

  /**
   * How long the cleared state is left on screen before moving on. Long enough for the last
   * card's removal and the dot filling to register as "done", short enough that it still reads
   * as going straight to the next patient rather than waiting.
   */
  static readonly AUTO_ADVANCE_DELAY_MS = 400;

  private autoAdvanceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private dialogRef: MatDialogRef<IcdReviewWizardComponent, IcdReviewWizardResult>,
    private router: Router,
    @Inject(MAT_DIALOG_DATA) public data: IcdReviewWizardData,
  ) {
    // ⚠ SNAPSHOT, NOT A LIVE LIST. The sequence is fixed when the wizard opens: a patient whose
    // queue you have just emptied stays in it, showing a cleared state. Dropping them as they are
    // finished would renumber everything under the user mid-task — "4 of 11" would silently
    // become a different patient — and reviewing is exactly the task where losing your place is
    // most costly.
    this.patients = [...(data?.patients || [])];
    this.exportLabel = data?.exportLabel || 'export';
  }

  /**
   * The embedded review surface exists only after the view is built, so the FIRST patient is
   * pointed at here rather than in the constructor. Its own `ngOnInit` deliberately loads nothing
   * when it has no patient, which is what makes this the single entry point for every patient in
   * the run — including the first.
   */
  ngAfterViewInit(): void {
    const first = this.current;
    if (first) {
      // Deferred a tick: `setPatient` sets `loading` and triggers a request, and doing that
      // during the same change-detection pass that created the child is the classic
      // ExpressionChangedAfterItHasBeenChecked.
      setTimeout(() => this.review?.setPatient(first.patientId, this.currentName));
    }
  }

  get current(): IcdReviewWizardPatient | null {
    return this.patients[this.index] || null;
  }

  get currentName(): string {
    const p = this.current;
    if (!p) return '';
    return `${p.name || ''} ${p.surname || ''}`.trim() || 'this patient';
  }

  ngOnDestroy(): void {
    this.cancelAutoAdvance();
  }

  get isFirst(): boolean { return this.index <= 0; }
  get isLast(): boolean { return this.index >= this.patients.length - 1; }

  /**
   * Every patient in the run is finished, so the interrupted export can be offered now.
   *
   * Reviewing out of order is common — the biller opens a file, comes back, jumps to another
   * dot — and the last patient to be CLEARED is frequently not the last one in the SEQUENCE.
   * Without this the export button only appeared on the final dot, so a finished run could still
   * need several Next clicks to reach it.
   */
  get allCleared(): boolean {
    return this.patients.length > 0 && this.patients.every((p) => this.isCleared(p));
  }

  /** Pending count for a patient as it stood when the wizard opened, for the progress strip. */
  countFor(p: IcdReviewWizardPatient): number {
    return Number(p?.counts?.total || 0);
  }

  /**
   * Has this patient's queue been worked to empty?
   *
   * ⚠ NOT `counts.total === 0`. That is the SNAPSHOT taken when the wizard opened, and the
   * summary only returns patients who had work — so it is non-zero for every patient in the run
   * and never changes as they are reviewed. Keying the dots off it left the whole strip hollow
   * however much you got through, which is the opposite of what a progress indicator is for.
   *
   * The patient on screen is read LIVE from the embedded review surface, so the dot fills the
   * moment the last decision is resolved rather than when you navigate away. Everyone else is
   * read from `clearedIds`, snapshotted on the way past.
   */
  isCleared(p: IcdReviewWizardPatient): boolean {
    if (!p) return false;
    if (this.current?.patientId === p.patientId) {
      return !!this.review && !this.review.loading && this.review.decisions.length === 0;
    }
    return this.clearedIds.has(p.patientId);
  }

  /**
   * Records whether the patient on screen is finished, before moving off them.
   *
   * Deliberately removes as well as adds: accepting everything and then dismissing a decision
   * that re-proposes something would otherwise leave a filled dot claiming work that is no longer
   * done. The set has to track the state, not just accumulate.
   */
  private snapshotCurrentCleared(): void {
    const p = this.current;
    if (!p || !this.review || this.review.loading) return;
    if (this.review.decisions.length === 0) this.clearedIds.add(p.patientId);
    else this.clearedIds.delete(p.patientId);
  }

  next(): void {
    if (this.isLast) return;
    this.goTo(this.index + 1);
  }

  previous(): void {
    if (this.isFirst) return;
    this.goTo(this.index - 1);
  }

  /**
   * The embedded review surface has just emptied the patient on screen through a decision.
   *
   * This is the "saves an extra click" path: finishing a patient moves straight on to the next
   * one that still has work, rather than leaving the biller looking at an empty queue and a Next
   * button. Only patients AFTER the current one are considered, so the fixed sequence still
   * reads left to right; a cleared patient earlier in the run was stepped back to deliberately.
   *
   * Nothing happens on the last patient with work: `allCleared` then surfaces the export button,
   * but running the export itself stays a click. Submitting billing is not something that
   * should happen because a dismiss request completed.
   */
  onQueueCleared(patientId: string): void {
    const p = this.current;
    if (!p || p.patientId !== patientId) return;
    this.cancelAutoAdvance();
    const target = this.nextPendingIndex();
    if (target < 0) return;
    this.autoAdvanceTimer = setTimeout(() => {
      this.autoAdvanceTimer = null;
      // The user may have moved themselves in the meantime; never advance from somewhere else.
      if (this.current?.patientId !== patientId) return;
      this.goTo(target);
    }, IcdReviewWizardComponent.AUTO_ADVANCE_DELAY_MS);
  }

  /** Index of the next patient after the current one whose queue is not known to be empty. */
  private nextPendingIndex(): number {
    for (let i = this.index + 1; i < this.patients.length; i++) {
      if (!this.isCleared(this.patients[i])) return i;
    }
    return -1;
  }

  private cancelAutoAdvance(): void {
    if (this.autoAdvanceTimer) {
      clearTimeout(this.autoAdvanceTimer);
      this.autoAdvanceTimer = null;
    }
  }

  goTo(index: number): void {
    if (index < 0 || index >= this.patients.length || index === this.index) return;
    // A manual move supersedes a pending automatic one.
    this.cancelAutoAdvance();
    // Carried forward before the embedded dialog is repointed: `setPatient` deliberately does not
    // reset `changed`, but reading it here keeps the wizard's own flag true even if that ever
    // changes.
    this.changed = this.changed || !!this.review?.changed;
    // Before the surface is repointed — after this it describes the NEXT patient.
    this.snapshotCurrentCleared();
    this.index = index;
    const p = this.current;
    if (p) this.review?.setPatient(p.patientId, this.currentName);
  }

  /**
   * Opens the patient file underneath the wizard.
   *
   * The dialog stays open — it is modeless and lives in an overlay — so the biller can read the
   * notes that decide a suggestion and come straight back to the queue.
   */
  openPatientFile(): void {
    const p = this.current;
    if (!p) return;
    this.router.navigate(['/patients', { outlets: { patient: ['file', p.patientId] } }]);
  }

  finish(runExport: boolean): void {
    this.cancelAutoAdvance();
    this.dialogRef.close({
      changed: this.changed || !!this.review?.changed,
      runExport,
    });
  }
}
